import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraSurface, SpecraTag, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQueryClient } from '@tanstack/react-query';
import { BookOpenCheck, Check, MessageSquare, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { SOPS_KEY, useSops } from '../../../hooks/useSops';
import { apiErrorMessage } from '../../../lib/api/apiErrorMessage';
import { diffLines } from '../../../lib/chat/sopFormat';
import { agentClient } from '../../../lib/connect/clients';
import { EnhancedMarkdown } from '../../EnhancedMarkdown';
import { useChatFeedback } from '../ChatFeedbackContext';
const COLLAPSED_LINES = 6;
export const sopAcceptFeedback = (scope, name)=>`User accepted: saved the ${scope} SOP ${name}. Continue with what they asked.`;
export const sopRejectFeedback = (name)=>`User declined saving the SOP ${name}. Nothing was saved. Ask what they want instead.`;
export const sopCommentFeedback = (text)=>`User feedback on the proposed SOP: ${text}`;
function SopDiff({ sop, previous }) {
    const lines = useMemo(()=>diffLines(previous.body ?? '', sop.body ?? ''), [
        previous.body,
        sop.body
    ]);
    const field = (label, before, after)=>(before ?? '') === (after ?? '') ? null : /*#__PURE__*/ _jsxs("div", {
            className: "text-xs",
            children: [
                /*#__PURE__*/ _jsxs("span", {
                    className: "font-medium",
                    children: [
                        label,
                        ": "
                    ]
                }),
                /*#__PURE__*/ _jsx("span", {
                    className: "text-danger line-through",
                    children: before || '(none)'
                }),
                ' ',
                /*#__PURE__*/ _jsx("span", {
                    className: "text-success",
                    children: after || '(none)'
                })
            ]
        });
    const changed = lines.some((l)=>l.kind !== 'same');
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col gap-1.5 border-t border-border px-4 py-3",
        children: [
            /*#__PURE__*/ _jsxs(SpecraText, {
                size: "meta",
                tone: "muted",
                children: [
                    "Compared with the ",
                    previous.layer ?? 'current',
                    " SOP"
                ]
            }),
            field('Title', previous.title, sop.title),
            field('When', previous.when, sop.when),
            changed ? /*#__PURE__*/ _jsx("pre", {
                className: "max-h-72 overflow-auto rounded-md bg-muted/40 p-2 font-mono text-xs leading-5",
                children: lines.map((l, i)=>/*#__PURE__*/ _jsxs("div", {
                        "data-diff": l.kind,
                        className: l.kind === 'add' ? 'bg-success-muted text-success' : l.kind === 'remove' ? 'bg-danger-muted text-danger' : 'text-muted-foreground',
                        children: [
                            l.kind === 'add' ? '+ ' : l.kind === 'remove' ? '- ' : '  ',
                            l.text
                        ]
                    }, i))
            }) : /*#__PURE__*/ _jsx(SpecraText, {
                size: "meta",
                tone: "muted",
                children: "The body is unchanged."
            })
        ]
    });
}
/**
 * An SOP the agent proposes: saved only when the human accepts, through the
 * memory RPC under their own principal (organization SOPs need an editor).
 */ export function SopWriteCard({ output }) {
    const [cardStatus, setStatus] = useState('pending');
    const [error, setError] = useState(null);
    const [showAll, setShowAll] = useState(false);
    const [showDiff, setShowDiff] = useState(false);
    const [showComment, setShowComment] = useState(false);
    const [comment, setComment] = useState('');
    const sendFeedback = useChatFeedback();
    const queryClient = useQueryClient();
    // A card reloaded from history forgets its click; the library remembers.
    // When the SOP saved at this scope already matches the proposal, show it
    // as saved instead of inviting a second accept.
    const sopsQuery = useSops();
    const proposedLayer = output.memory?.scopeKind === 'tenant' ? 'organization' : 'personal';
    const alreadySaved = useMemo(()=>(sopsQuery.data ?? []).some((r)=>r.layer === proposedLayer && r.name === output.sop?.name && r.body.trim() === (output.sop?.body ?? '').trim()), [
        sopsQuery.data,
        proposedLayer,
        output.sop?.name,
        output.sop?.body
    ]);
    const status = cardStatus === 'pending' && alreadySaved ? 'saved' : cardStatus;
    const sop = output.sop;
    const memory = output.memory;
    if (!output.success || !sop || !memory) {
        return /*#__PURE__*/ _jsx(SpecraSurface, {
            elevation: "xs",
            className: "px-4 py-3",
            "data-testid": Testids.assistant.sopCard,
            children: /*#__PURE__*/ _jsx(SpecraText, {
                size: "body-sm",
                tone: "danger",
                children: output.error || 'The SOP proposal could not be prepared.'
            })
        });
    }
    const scope = output.scope ?? (memory.scopeKind === 'tenant' ? 'organization' : 'personal');
    const bodyLines = (sop.body ?? '').split('\n');
    const long = bodyLines.length > COLLAPSED_LINES;
    const shownBody = long && !showAll ? bodyLines.slice(0, COLLAPSED_LINES).join('\n') : sop.body;
    const accept = async ()=>{
        setStatus('saving');
        setError(null);
        try {
            await agentClient.upsertMemory(memory);
        } catch (err) {
            setError(apiErrorMessage(err, 'Could not save the SOP.'));
            setStatus('pending');
            return;
        }
        setStatus('saved');
        void queryClient.invalidateQueries({
            queryKey: [
                SOPS_KEY
            ]
        });
        sendFeedback(sopAcceptFeedback(scope, sop.name));
    };
    const reject = ()=>{
        setStatus('rejected');
        sendFeedback(sopRejectFeedback(sop.name));
    };
    const sendComment = ()=>{
        const text = comment.trim();
        if (!text) return;
        setStatus('commented');
        setShowComment(false);
        sendFeedback(sopCommentFeedback(text));
    };
    const pending = status === 'pending' || status === 'saving';
    return /*#__PURE__*/ _jsxs(SpecraSurface, {
        elevation: "xs",
        className: "overflow-hidden",
        "data-testid": Testids.assistant.sopCard,
        "data-state": status,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-2 border-b border-border px-4 py-3",
                children: [
                    /*#__PURE__*/ _jsx(BookOpenCheck, {
                        className: "h-4 w-4 text-primary",
                        "aria-hidden": "true"
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "body-sm",
                        className: "font-medium",
                        children: "Proposed SOP"
                    }),
                    /*#__PURE__*/ _jsx(SpecraTag, {
                        tone: scope === 'organization' ? 'warning' : 'info',
                        children: scope === 'organization' ? 'Organization' : 'Personal'
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "font-mono text-xs text-muted-foreground",
                        children: sop.name
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col gap-1.5 px-4 py-3",
                children: [
                    sop.title && sop.title !== sop.name ? /*#__PURE__*/ _jsx(SpecraText, {
                        size: "body-sm",
                        className: "font-medium",
                        children: sop.title
                    }) : null,
                    output.summary ? /*#__PURE__*/ _jsx(SpecraText, {
                        size: "body-sm",
                        children: output.summary
                    }) : null,
                    output.reason ? /*#__PURE__*/ _jsxs(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        children: [
                            "Why: ",
                            output.reason
                        ]
                    }) : null,
                    sop.when ? /*#__PURE__*/ _jsxs(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        children: [
                            "Used when: ",
                            sop.when
                        ]
                    }) : null,
                    output.note ? /*#__PURE__*/ _jsx(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        children: output.note
                    }) : null
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "border-t border-border px-4 py-3",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "relative",
                        children: [
                            /*#__PURE__*/ _jsx(EnhancedMarkdown, {
                                content: shownBody ?? '',
                                className: "prose-sm",
                                density: "chat"
                            }),
                            long && !showAll ? /*#__PURE__*/ _jsx("div", {
                                className: "pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-card"
                            }) : null
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mt-1 flex flex-wrap gap-3",
                        children: [
                            long ? /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "link",
                                size: "inline",
                                onClick: ()=>setShowAll((v)=>!v),
                                children: showAll ? 'Show less' : 'Show all'
                            }) : null,
                            output.previous ? /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "link",
                                size: "inline",
                                "aria-expanded": showDiff,
                                "data-testid": Testids.assistant.sopDiffToggle,
                                onClick: ()=>setShowDiff((v)=>!v),
                                children: showDiff ? 'Hide changes' : 'Changes'
                            }) : null
                        ]
                    })
                ]
            }),
            showDiff && output.previous ? /*#__PURE__*/ _jsx(SopDiff, {
                sop: sop,
                previous: output.previous
            }) : null,
            output.needsEditor && pending ? /*#__PURE__*/ _jsx("div", {
                className: "border-t border-border bg-warning-muted px-4 py-2",
                children: /*#__PURE__*/ _jsx(SpecraText, {
                    size: "meta",
                    tone: "warning",
                    children: "Only an admin or Company Settings admin can accept an organization SOP."
                })
            }) : null,
            pending ? /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-2 border-t border-border bg-muted/40 px-4 py-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        "data-testid": Testids.assistant.sopAccept,
                        onClick: accept,
                        isLoading: status === 'saving',
                        disabled: status === 'saving',
                        iconLeft: /*#__PURE__*/ _jsx(Check, {}),
                        children: "Accept & save"
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "destructive",
                        "data-testid": Testids.assistant.sopReject,
                        onClick: reject,
                        disabled: status === 'saving',
                        iconLeft: /*#__PURE__*/ _jsx(X, {}),
                        children: "Reject"
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "ghost",
                        "data-testid": Testids.assistant.sopComment,
                        onClick: ()=>setShowComment(true),
                        disabled: status === 'saving',
                        iconLeft: /*#__PURE__*/ _jsx(MessageSquare, {}),
                        children: "Comment"
                    })
                ]
            }) : null,
            showComment && pending ? /*#__PURE__*/ _jsxs("div", {
                className: "border-t border-border bg-muted/40 px-4 py-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: comment,
                        onChange: (e)=>setComment(e.target.value),
                        placeholder: "Tell the AI what to change...",
                        "aria-label": "Feedback on the proposed SOP",
                        rows: 2
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mt-1.5 flex justify-end gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                onClick: ()=>setShowComment(false),
                                children: "Cancel"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                onClick: sendComment,
                                disabled: !comment.trim(),
                                children: "Send feedback"
                            })
                        ]
                    })
                ]
            }) : null,
            error ? /*#__PURE__*/ _jsx("div", {
                role: "alert",
                className: "border-t border-border bg-danger-muted px-4 py-2",
                "data-testid": Testids.assistant.sopError,
                children: /*#__PURE__*/ _jsx(SpecraText, {
                    size: "meta",
                    tone: "danger",
                    children: error
                })
            }) : null,
            status === 'saved' ? /*#__PURE__*/ _jsx("div", {
                className: "border-t border-border bg-success-muted px-4 py-2",
                children: /*#__PURE__*/ _jsxs(SpecraText, {
                    size: "meta",
                    tone: "success",
                    children: [
                        "✓ Saved the ",
                        scope,
                        " SOP ",
                        sop.name
                    ]
                })
            }) : null,
            status === 'rejected' ? /*#__PURE__*/ _jsx("div", {
                className: "border-t border-border bg-muted px-4 py-2",
                children: /*#__PURE__*/ _jsx(SpecraText, {
                    size: "meta",
                    tone: "muted",
                    children: "✗ Declined; nothing was saved"
                })
            }) : null,
            status === 'commented' ? /*#__PURE__*/ _jsx("div", {
                className: "border-t border-border bg-muted px-4 py-2",
                children: /*#__PURE__*/ _jsx(SpecraText, {
                    size: "meta",
                    tone: "muted",
                    children: "💬 Feedback sent — waiting for AI"
                })
            }) : null
        ]
    });
}
